import React from "react";
import { Button, Tag, Space, Progress, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faTrash, faPen, faUser } from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";

export const STATUS_TAG = {
    draft: { color: "blue", label: "Nháp" },
    sending: { color: "processing", label: "Đang gửi" },
    done: { color: "green", label: "Hoàn thành" },
    failed: { color: "red", label: "Thất bại" },
};

const fmtTime = (ts) => (ts ? format(new Date(Number(ts)), "dd/MM/yy HH:mm") : null);

/** Nhãn ngắn cho đối tượng nhận — chi tiết bộ lọc xem ở trang chi tiết. */
function AudienceTag({ record }) {
    if (record.audienceType === "all") return <Tag color="geekblue">Tất cả</Tag>;
    if (record.audienceType === "ids") {
        let ap = record.audiencePayload;
        if (typeof ap === "string") { try { ap = JSON.parse(ap); } catch { ap = null; } }
        const n = ap?.userIds?.length;
        return <Tag color="orange">{n ? `${n.toLocaleString()} user chọn tay` : "Danh sách ID"}</Tag>;
    }
    if (record.audienceType === "filtered") return <Tag color="purple">Bộ lọc</Tag>;
    return <Tag>{record.audienceType || "-"}</Tag>;
}

/**
 * Tiến độ gửi: thanh xanh = đã gửi, phần đỏ = lỗi, phần xám = còn trong hàng đợi.
 * Nháp chưa resolve người nhận nên chưa có mục tiêu — chỉ ghi chú.
 */
function SendProgress({ record }) {
    const target = Number(record.targetUserCount) || 0;
    const sent = Number(record.sentCount) || 0;
    const failed = Number(record.failedCount) || 0;
    if (record.status === "draft" || !target) return <span className="text-content-3 text-xs">Chưa gửi</span>;

    const pct = (n) => Math.min(100, Math.round((n / target) * 1000) / 10);
    const processed = pct(sent + failed);
    return (
        <Tooltip title={`Đã gửi ${sent.toLocaleString()} · Lỗi ${failed.toLocaleString()} · Mục tiêu ${target.toLocaleString()}`}>
            <div style={{ minWidth: 170 }}>
                <Progress
                    percent={processed}
                    success={{ percent: pct(sent) }}
                    strokeColor="#ff4d4f"
                    size="small"
                    status={record.status === "sending" ? "active" : "normal"}
                    format={() => <span className="text-xs">{processed}%</span>}
                />
                <div className="text-xs text-content-3 -mt-1">
                    <span className="text-green-600 font-medium">{sent.toLocaleString()}</span>
                    {" / "}{target.toLocaleString()}
                    {failed > 0 && <span className="text-red-600 ml-2">{failed.toLocaleString()} lỗi</span>}
                </div>
            </div>
        </Tooltip>
    );
}

/**
 * Cột bảng danh sách chiến dịch. Tách khỏi EmailCampaignList để file trang gọn.
 * handlers: { navigate, onSend, onDelete, sendingId, deletingId }
 */
export function buildEmailCampaignColumns({ navigate, onSend, onDelete, sendingId, deletingId }) {
    return [
        {
            title: "Chiến dịch",
            key: "campaign",
            fixed: "left",
            render: (_, r) => (
                <div style={{ maxWidth: 300 }}>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a className="font-semibold block truncate" onClick={() => navigate(`/email/${r.campaignId}`)}>{r.name}</a>
                    <div className="text-xs text-content-2 truncate" title={r.subject}>✉ {r.subject}</div>
                    {r.createdBy && (
                        <div className="text-[11px] text-content-3 mt-0.5">
                            <FontAwesomeIcon icon={faUser} className="mr-1" />{r.createdBy}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: "Trạng thái",
            dataIndex: "status",
            width: 120,
            render: (s) => {
                const t = STATUS_TAG[s] || { color: "default", label: s };
                return <Tag color={t.color}>{t.label}</Tag>;
            },
        },
        { title: "Đối tượng", key: "audience", width: 150, render: (_, r) => <AudienceTag record={r} /> },
        { title: "Tiến độ gửi", key: "progress", width: 200, render: (_, r) => <SendProgress record={r} /> },
        {
            title: "Thời gian",
            key: "time",
            width: 150,
            render: (_, r) => (
                <div className="text-xs leading-5 whitespace-nowrap">
                    <div><span className="text-content-3">Tạo:</span> {fmtTime(r.createdAt) || "-"}</div>
                    {r.sentAt && <div><span className="text-content-3">Gửi:</span> {fmtTime(r.sentAt)}</div>}
                </div>
            ),
        },
        {
            title: "",
            key: "actions",
            width: 190,
            // Ghim phải: màn ~1470px bảng rộng hơn khung → không ghim thì nút Gửi bị cắt.
            fixed: "right",
            render: (_, r) => (
                <Space size={4}>
                    <Button size="small" onClick={() => navigate(`/email/${r.campaignId}`)}>Chi tiết</Button>
                    {r.status === "draft" && (
                        <Tooltip title="Sửa">
                            <Button size="small" icon={<FontAwesomeIcon icon={faPen} />} onClick={() => navigate(`/email/${r.campaignId}/edit`)} />
                        </Tooltip>
                    )}
                    {r.status === "draft" && (
                        <Button
                            size="small"
                            type="primary"
                            icon={<FontAwesomeIcon icon={faPaperPlane} />}
                            loading={sendingId === r.campaignId}
                            onClick={() => onSend(r)}
                        >
                            Gửi
                        </Button>
                    )}
                    {r.status !== "sending" && (
                        <Tooltip title="Xóa">
                            <Button
                                size="small"
                                danger
                                type="text"
                                icon={<FontAwesomeIcon icon={faTrash} />}
                                loading={deletingId === r.campaignId}
                                onClick={() => onDelete(r)}
                            />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];
}
